import { SxProps, Theme, Tooltip } from '@mui/material';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { HOVER_MEDIA } from '../../../../../constants/ui';
import { ICampaignItem } from '../../../../../types/pyg';
import { isActivationKey } from '../../../../../util/ui';
import LightboxBackgroundImage from '../../LightboxBackgroundImage';
import { cornerChipSx, fillMediaSx, SkuTile, SkuTileMedia, SkuTileName } from '../Card.styles';
import { QuantityChip } from './QuantityChip';

interface IProps {
  item: ICampaignItem;
  index: number;
  onMediaClick?: (index: number) => void;
  onNameClick?: (index: number) => void;
}

const clickableNameSx: SxProps<Theme> = {
  cursor: 'pointer',
  borderRadius: 0.5,
  [HOVER_MEDIA]: { '&:hover': { textDecoration: 'underline' } },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
};

export const SmallCardImage: React.FC<IProps> = ({ item, index, onMediaClick, onNameClick }) => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const { quantity, image_url, name } = item;

  const handleImageBackgroundClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onMediaClick?.(index);
    },
    [onMediaClick, index],
  );

  const handleNameClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onNameClick?.(index);
    },
    [onNameClick, index],
  );

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isActivationKey(e)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      onNameClick?.(index);
    },
    [onNameClick, index],
  );

  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) {
      return;
    }
    setIsTruncated(el.scrollWidth > el.clientWidth + 1);
  }, [name]);

  const isNameClickable = !!onNameClick;

  const nameNode = (
    <SkuTileName
      ref={nameRef}
      role={isNameClickable ? 'button' : undefined}
      tabIndex={isNameClickable ? 0 : undefined}
      aria-label={isNameClickable ? `View details for ${name}` : undefined}
      onClick={isNameClickable ? handleNameClick : undefined}
      onKeyDown={isNameClickable ? handleNameKeyDown : undefined}
      sx={isNameClickable ? clickableNameSx : undefined}
    >
      {name}
    </SkuTileName>
  );

  return (
    <SkuTile>
      <QuantityChip quantity={quantity ?? 0} sx={cornerChipSx} />
      <SkuTileMedia onClick={handleImageBackgroundClick}>
        <LightboxBackgroundImage
          url={image_url}
          asImage
          alt={name}
          stopPropagation
          isScalable={false}
          sx={fillMediaSx}
        />
      </SkuTileMedia>
      {isTruncated ? (
        <Tooltip title={name} placement="top" arrow>
          {nameNode}
        </Tooltip>
      ) : (
        nameNode
      )}
    </SkuTile>
  );
};
